import { mapColor } from '@/utils/colors';

export type Cell = { content: string; className: string };

export const color = (value?: string) => mapColor(value) || value;

type SpanExtent = { start: number; openEnd: number; contentEnd: number; end: number };

/** Find the matching </span> for a span opened just before openEnd, surviving nested spans. */
const matchBalancedSpan = (html: string, openEnd: number) => {
    const tags = /<\/?span\b[^>]*>/gi;
    tags.lastIndex = openEnd;
    let depth = 1;
    for (let m = tags.exec(html); m; m = tags.exec(html)) {
        depth += m[0][1] === '/' ? -1 : 1;
        if (depth === 0) {
            return { contentEnd: m.index, end: tags.lastIndex };
        }
    }
    return null;
};

const collectItemSpans = (html: string): SpanExtent[] | null => {
    // Tolerant of extra attributes and combined class lists, e.g. when the
    // line also carries a text-color or highlight format.
    const open = /<span\b[^>]*\bclass="(?:[^"]*\s)?cell-list-item(?:\s[^"]*)?"[^>]*>/gi;
    const spans: SpanExtent[] = [];
    for (let m = open.exec(html); m; m = open.exec(html)) {
        const balanced = matchBalancedSpan(html, open.lastIndex);
        if (!balanced) {
            return null;
        }
        spans.push({ start: m.index, openEnd: open.lastIndex, ...balanced });
        open.lastIndex = balanced.end;
    }
    return spans;
};

const VOID_TAGS = new Set(['br', 'img', 'hr', 'wbr', 'input', 'area', 'col', 'embed', 'source', 'track']);

/** Split on <br> at nesting depth 0 only, so breaks inside nested tags (spans, bold, links…) survive. */
const splitTopLevelBreaks = (content: string) => {
    const tokens = /<\/?([a-z][a-z0-9-]*)\b[^>]*>/gi;
    const parts: string[] = [];
    let depth = 0;
    let last = 0;
    for (let m = tokens.exec(content); m; m = tokens.exec(content)) {
        const tag = m[1].toLowerCase();
        if (m[0][1] === '/') {
            depth = Math.max(0, depth - 1);
        } else if (tag === 'br') {
            if (depth === 0) {
                parts.push(content.slice(last, m.index));
                last = tokens.lastIndex;
            }
        } else if (!VOID_TAGS.has(tag) && !m[0].endsWith('/>')) {
            depth++;
        }
    }
    parts.push(content.slice(last));
    return parts;
};

const isRunSeparator = (between: string) => /^\s*(?:<br[^>]*>\s*)?$/i.test(between);

/** Cell lines marked with the editor's "List item" format become a real <ul>. */
export const transformCellLists = (html: string) => {
    if (!/cell-list-item/i.test(html)) {
        return html;
    }

    const spans = collectItemSpans(html);
    if (!spans || spans.length === 0) {
        // Unbalanced markup — leave the html untouched rather than corrupt it.
        return html;
    }

    let out = '';
    let lastEnd = 0;
    let i = 0;
    while (i < spans.length) {
        let j = i;
        while (j + 1 < spans.length && isRunSeparator(html.slice(spans[j].end, spans[j + 1].start))) {
            j++;
        }

        const items = spans
            .slice(i, j + 1)
            .flatMap(span => splitTopLevelBreaks(html.slice(span.openEnd, span.contentEnd)))
            .map(item => item.trim())
            .filter(Boolean);

        // A run of only empty items: keep the original markup instead of an empty <ul>
        if (items.length === 0) {
            out += html.slice(lastEnd, spans[j].end);
            lastEnd = spans[j].end;
            i = j + 1;
            continue;
        }

        // The <ul> is a block of its own, so swallow one line break on each side —
        // unless the break is all that separates this run from the previous one,
        // in which case it is a deliberate blank line between two lists.
        let runStart = spans[i].start;
        const between = html.slice(lastEnd, runStart);
        const isGapBetweenRuns = i > 0 && isRunSeparator(between);
        if (!isGapBetweenRuns) {
            const before = between.match(/<br[^>]*>\s*$/i);
            if (before) {
                runStart -= before[0].length;
            }
        }
        let runEnd = spans[j].end;
        const after = html.slice(runEnd).match(/^\s*<br[^>]*>/i);
        if (after) {
            runEnd += after[0].length;
        }

        out += html.slice(lastEnd, runStart);
        out += `<ul class="cell-list">${items.map(item => `<li>${item}</li>`).join('')}</ul>`;
        lastEnd = runEnd;
        i = j + 1;
    }
    return out + html.slice(lastEnd);
};

export const cleanHtml = (html: string) => transformCellLists(html.replace(/<strong>/gi, '<b>').replace(/<\/strong>/gi, '</b>'));

export const parseTable = (html: string) => {
    const extractCells = (rowHtml: string): Cell[] =>
        [...rowHtml.matchAll(/<t[dh]([^>]*)>([\s\S]*?)<\/t[dh]>/gi)].map(m => ({
            content: m[2].trim(),
            className: m[1].match(/class="([^"]*)"/)?.[1] || '',
        }));

    const extractSection = (tag: string) => {
        const match = html.match(new RegExp(`<${tag}[^>]*>([\\s\\S]*?)</${tag}>`, 'i'));
        return match ? [...match[1].matchAll(/<tr[^>]*>([\s\S]*?)<\/tr>/gi)].map(r => extractCells(r[1])) : [];
    };

    return {
        head: extractSection('thead'),
        body: extractSection('tbody'),
        foot: extractSection('tfoot'),
        hasFixedLayout: /<table[^>]*class="[^"]*has-fixed-layout/.test(html),
    };
};
